// @flow
import { AdSlot, Grid, GridItem, Query, SeoData, SortableTable, } from '@haaretz/htz-components';
import React from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AssetsFilter from '../../components/AssetsFilter/AssetsFilter';
import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import etfFiltersQuery from '../queries/etfFilters.graphql';
import etfInitQuery from '../queries/etfInitQuery.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const subFilters = (fields, value, title, miscStyles) => ({
  title,
  value,
  miscStyles,
  fields: fields.map(field => ({
    display: field.value,
    value: {
      queryString: field.id,
      ...(field.etfCategoryPosition
        ? {
          subFilters: subFilters(
            field.etfCategoryPosition,
            'filter3',
            'בחר פוזיציה: ',
            { flexShrink: '0', }
          ),
        }
        : {}),
    },
  })),
});

const filtersDisplay = new Map([
  [ 'etfIsraelIndices', 'מניות בישראל', ],
  [ 'etfForeignStocks', 'מניות בחו"ל', ],
  [ 'etfCPI', 'מדדים', ],
  [ 'etfILS', 'שקלי', ],
  [ 'etfCurrencies', 'מט"ח', ],
  [ 'etfCommodities', 'סחורות', ],
]);

const buildFilterObj = filters => ({
  title: 'בחר אפיק השקעות:',
  value: 'filter1',
  miscStyles: { flexShrink: '0', },
  fields: Object.keys(filters)
    .filter(filter => filter !== '__typename' && filters[filter].length)
    .map(filter => ({
      display: filtersDisplay.get(filter) || '',
      value: {
        queryString: filter,
        subFilters: subFilters(
          filters[filter],
          'filter2',
          'בחר מדד: ',
          { width: [ { from: 's', until: 'xl', value: '35rem', }, { from: 'xl', value: '28rem', }, ], flexShrink: '0', }
        ),
      },
    })),
});

const numToString: number => string = num => num.toLocaleString('he', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function etf({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title="קרנות סל - TheMarker Finance"
        description="כל המידע על  קרנות סל: נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance"
        path={asPath}
      />
      <FelaTheme>
        {theme => (
          <FelaComponent
            style={{
              extend: [
                theme.mq(
                  { until: 'l', },
                  {
                    display: 'flex',
                    flexDirection: 'column',
                  }
                ),
              ],
            }}
          >
            <PageRow miscStyles={{ order: '1', }}>
              <RowItem title="קרנות סל">
                <Query query={etfInitQuery}>
                  {({ loading, error, data, client, }) => {
                    if (loading) return null;
                    if (error) return null;

                    const preChange = (filters, callback) => (
                      client
                        .query({
                          query: etfFiltersQuery,
                          variables: {
                            filters: [
                              {
                                section: filters[0].value,
                                subSection: filters[1].value,
                              },
                              {
                                section: 'etfCategoryPosition',
                                subSection: filters[2].value,
                              },
                            ],
                            count: 11,
                            sortBy: 'name',
                            sortOrder: 'asc',
                            offset: 0,
                          },
                          fetchPolicy: 'network-only',
                        })
                        .then(() => {
                          callback();
                        })
                    );

                    return (
                      <AssetsFilter
                        filters={buildFilterObj(data.metadata)}
                        depth={3}
                        preChange={preChange}
                      >
                        {({ filters, }) => (
                          <SortableTable
                            miscStyles={{
                              marginTop: [
                                { until: 's', value: '21rem', },
                                { from: 's', value: '7rem', },
                              ],
                              tableLayout: 'auto',
                            }}
                            filters={[
                              {
                                section: filters[0].value,
                                subSection: filters[1].value,
                              },
                              {
                                section: 'etfCategoryPosition',
                                subSection: filters[2].value,
                              },
                            ]}
                            loadMore
                            {...filters.reduce((obj, item) => {
                              // eslint-disable-next-line no-param-reassign
                              obj[item.key] = item.value.toString();
                              return obj;
                            }, {})}
                            type="mtf"
                            fragment="
                              name
                              value
                              changePercentage
                              volume
                            "
                            fields={[
                              {
                                name: 'name',
                                display: 'שם הקרן',
                                sortingOrder: 'asc',
                                style: () => ({
                                  fontWeight: '700',
                                  maxWidth: '17rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }),
                                value: ({ name, }) => name || '-',
                              },
                              {
                                name: 'value',
                                display: 'שער אחרון',
                                sortingOrder: 'desc',
                                value: ({ value, }) => (value ? numToString(value) : '-'),
                              },
                              {
                                name: 'changePercentage',
                                display: '% שינוי',
                                sortingOrder: 'desc',
                                style: ({ changePercentage, }) => ({
                                  ...(
                                    changePercentage
                                      ? {
                                        color:
                                          changePercentage < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                      }
                                      : {}
                                  ),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingEnd: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                }),
                                value: ({ changePercentage, }) => `
                                  ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                                  ${changePercentage ? `${numToString(Math.abs(changePercentage))}%` : '-'}
                                `,
                              },
                              {
                                name: 'volume',
                                display: 'מחזור (א׳ ש״ח)',
                                sortingOrder: 'desc',
                                hideUntil: [ 's', ],
                                value: ({ volume, }) => (volume ? numToString(volume) : '-'),
                              },
                            ]}
                            initialSort="name"
                            count={11}
                          />
                        )}
                      </AssetsFilter>
                    );
                  }}
                </Query>
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ order: '3', }}>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ order: '2', }}>
              <Grid>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="ביצועי התעודות הנמוכות בשוק">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'etf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        yearlyYield
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '65%', },
                              { from: 's', until: 'l', value: '83%', },
                              { from: 'l', value: '70%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name || '-',
                        },
                        {
                          name: 'yearlyYield',
                          display: 'תשואה שנתית',
                          sortingOrder: 'asc',
                          style: ({ yearlyYield, }) => ({
                            ...(
                              yearlyYield
                                ? {
                                  color:
                                    yearlyYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                }
                                : {}
                            ),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'center',
                          }),
                          value: ({ yearlyYield, }) => `
                            ${yearlyYield > 0 ? '' : yearlyYield < 0 ? '-' : ''}
                            ${yearlyYield ? `${numToString(Math.abs(yearlyYield))}%` : '-'}
                          `,
                        },
                      ]}
                      initialSort="yearlyYield"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                >
                  <RowItem title="ביצועי התעודות הגבוהות בשוק">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'etf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        yearlyYield
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '65%', },
                              { from: 's', until: 'l', value: '83%', },
                              { from: 'l', value: '70%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name || '-',
                        },
                        {
                          name: 'yearlyYield',
                          display: 'תשואה שנתית',
                          sortingOrder: 'desc',
                          style: ({ yearlyYield, }) => ({
                            ...(
                              yearlyYield
                                ? {
                                  color:
                                    yearlyYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                }
                                : {}
                            ),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'center',
                          }),
                          value: ({ yearlyYield, }) => `
                            ${yearlyYield > 0 ? '' : yearlyYield < 0 ? '-' : ''}
                            ${yearlyYield ? `${numToString(Math.abs(yearlyYield))}%` : '-'}
                          `,
                        },
                      ]}
                      initialSort="yearlyYield"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow miscStyles={{ order: '4', }}>
              <Grid>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                  miscStyles={{ marginBottom: [ { until: 'l', value: '4rem', }, ], }}
                >
                  <RowItem title="התעודות עם דמי הניהול הכי נמוכים">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'etf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        managementFee
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'asc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '77%', },
                              { from: 's', until: 'l', value: '88%', },
                              { from: 'l', value: '78%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name || '-',
                        },
                        {
                          name: 'managementFee',
                          display: 'דמי ניהול',
                          sortingOrder: 'asc',
                          style: () => ({
                            textAlign: 'center',
                          }),
                          value: ({ managementFee, }) => (managementFee ? `${numToString(managementFee)}%` : '-'),
                        },
                      ]}
                      initialSort="managementFee"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
                <GridItem
                  width={[
                    { until: 'l', value: 1, },
                    { from: 'l', value: 1 / 2, },
                  ]}
                >
                  <RowItem title="התעודות עם דמי הניהול הכי גבוהים">
                    <SortableTable
                      filters={[ { section: 'type', subSection: 'etf', }, ]}
                      type="mtf"
                      fragment="
                        name
                        managementFee
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם הקרן',
                          sortingOrder: 'desc',
                          headerStyle: {
                            width: [
                              { until: 's', value: '77%', },
                              { from: 's', until: 'l', value: '88%', },
                              { from: 'l', value: '78%', },
                            ],
                          },
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            paddingStart: '2rem',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name || '-',
                        },
                        {
                          name: 'managementFee',
                          display: 'דמי ניהול',
                          sortingOrder: 'desc',
                          style: () => ({
                            textAlign: 'center',
                          }),
                          value: ({ managementFee, }) => (managementFee ? `${numToString(managementFee)}%` : '-'),
                        },
                      ]}
                      initialSort="managementFee"
                      count={9}
                    />
                  </RowItem>
                </GridItem>
              </Grid>
            </PageRow>
            <PageRow miscStyles={{ order: '5', }}>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
          </FelaComponent>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default etf;
